import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import { retrieveNews, NewsHighlight, NewsDetail } from '../../services/newsService';
import './ManageNews.css';

interface ManageNewsProps {
    toastRef: React.RefObject<Toast | null>;
}

const ManageNews: React.FC<ManageNewsProps> = ({ toastRef }) => {
    const [newsList, setNewsList] = useState<NewsHighlight[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState<any>(undefined);
    const [editingNews, setEditingNews] = useState<{ [key: string]: NewsDetail }>({});
    
    // For new news dialog
    const [showNewDialog, setShowNewDialog] = useState(false);
    const [newNews, setNewNews] = useState<Partial<NewsDetail>>({ title: '', contentMarkdown: '', author: { name: 'Admin', avatarUrl: '' }, isHighlight: false });
    const [saving, setSaving] = useState(false);

    const closeNewEditor = () => {
        setShowNewDialog(false);
        setNewNews({ title: '', contentMarkdown: '', author: { name: 'Admin', avatarUrl: '' }, isHighlight: false });
    };

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/getNewsList');
            if (res.ok) {
                const data = await res.json();
                setNewsList(data);
            }
        } catch (e) {
            toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore nel caricamento notizie' });
        } finally {
            setLoading(false);
        }
    };

    const loadNewsDetail = async (id: string) => {
        if (!editingNews[id]) {
            const detail = await retrieveNews(id);
            if (detail) {
                setEditingNews(prev => ({ ...prev, [id]: detail }));
            }
        }
    };

    const onRowExpand = async (e: any) => {
        const news = e.data as NewsHighlight;
        await loadNewsDetail(news.id);
    };

    const handleSaveNew = async () => {
        if (!newNews.title || !newNews.contentMarkdown) {
            toastRef.current?.show({ severity: 'warn', summary: 'Attenzione', detail: 'Titolo e contenuto sono obbligatori' });
            return;
        }
        setSaving(true);
        try {
            const res = await fetch('/api/createNews', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newNews)
            });
            if (res.ok) {
                toastRef.current?.show({ severity: 'success', summary: 'Successo', detail: 'Notizia creata' });
                closeNewEditor();
                fetchNews();
            } else {
                toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Creazione fallita' });
            }
        } catch (e) {
            toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (id: string) => {
        const detail = editingNews[id];
        try {
            const res = await fetch('/api/updateNews', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(detail)
            });
            if (res.ok) {
                toastRef.current?.show({ severity: 'success', summary: 'Successo', detail: 'Notizia aggiornata' });
                fetchNews();
            } else {
                toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Aggiornamento fallito' });
            }
        } catch (e) {
            toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Sei sicuro di voler eliminare questa notizia?")) return;
        try {
            const res = await fetch(`/api/deleteNews?id=${id}`, { method: 'DELETE' });
            if (res.ok) {
                toastRef.current?.show({ severity: 'success', summary: 'Successo', detail: 'Notizia eliminata' });
                fetchNews();
            }
        } catch (e) {
            toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
        }
    };

    const handleToggleHighlight = async (newsId: string, newStatus: boolean) => {
        // Optimistic UI update
        setNewsList(prev => prev.map(n => n.id === newsId ? { ...n, isHighlight: newStatus } : n));
        if (editingNews[newsId]) {
            setEditingNews(prev => ({ ...prev, [newsId]: { ...prev[newsId], isHighlight: newStatus } }));
        }

        try {
            // we need full detail to update, or just load it if missing
            let detail = editingNews[newsId];
            if (!detail) {
                detail = await retrieveNews(newsId) as NewsDetail;
            }
            if (!detail) return;

            const updatedDetail = { ...detail, isHighlight: newStatus };
            
            const res = await fetch('/api/updateNews', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedDetail)
            });

            if (res.ok) {
                toastRef.current?.show({ severity: 'success', summary: 'Successo', detail: 'Stato evidenza aggiornato', life: 3000 });
            } else {
                toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Aggiornamento fallito' });
                fetchNews(); // revert
            }
        } catch (e) {
            toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
            fetchNews(); // revert
        }
    };

    const myUploader = async (event: any, isNew: boolean, newsId?: string) => {
        const file = event.files[0];
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch('/api/uploadFile', {
                method: 'POST',
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                const url = data.url;
                const isImage = file.type.startsWith('image/');
                
                toastRef.current?.show({ severity: 'success', summary: 'Successo', detail: 'File caricato' });
                
                if (isNew) {
                    if (isImage) {
                        setNewNews(prev => ({ 
                            ...prev, 
                            contentMarkdown: prev.contentMarkdown + `\n\n![${file.name}](${url})` 
                        }));
                    } else {
                        setNewNews(prev => ({
                            ...prev,
                            attachments: [...(prev.attachments || []), { name: file.name, url }]
                        }));
                    }
                } else if (newsId) {
                    if (isImage) {
                        setEditingNews(prev => ({
                            ...prev,
                            [newsId]: {
                                ...prev[newsId],
                                contentMarkdown: prev[newsId].contentMarkdown + `\n\n![${file.name}](${url})`
                            }
                        }));
                    } else {
                        setEditingNews(prev => ({
                            ...prev,
                            [newsId]: {
                                ...prev[newsId],
                                attachments: [...(prev[newsId].attachments || []), { name: file.name, url }]
                            }
                        }));
                    }
                }
                event.options.clear();
            } else {
                toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Caricamento fallito' });
            }
        } catch (e) {
            toastRef.current?.show({ severity: 'error', summary: 'Errore', detail: 'Errore di rete' });
        }
    };

    const removeAttachment = (isNew: boolean, newsId: string | undefined, index: number) => {
        if (isNew) {
            setNewNews(prev => {
                const newAttachments = [...(prev.attachments || [])];
                newAttachments.splice(index, 1);
                return { ...prev, attachments: newAttachments };
            });
        } else if (newsId) {
            setEditingNews(prev => {
                const newAttachments = [...(prev[newsId].attachments || [])];
                newAttachments.splice(index, 1);
                return { ...prev, [newsId]: { ...prev[newsId], attachments: newAttachments } };
            });
        }
    };

    const renderAttachments = (attachments: {name: string, url: string}[] | undefined, isNew: boolean, newsId?: string) => {
        if (!attachments || attachments.length === 0) return null;
        return (
            <div className="mt-3">
                <span className="font-bold block mb-2 text-sm">Allegati Caricati:</span>
                <div className="flex flex-wrap gap-2">
                    {attachments.map((att, idx) => (
                        <div key={idx} className="flex align-items-center surface-card border-1 border-300 border-round p-2">
                            <i className="pi pi-file mr-2 text-primary"></i>
                            <a href={att.url} target="_blank" rel="noopener noreferrer" className="mr-2 text-color text-sm text-overflow-ellipsis" style={{maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden'}} title={att.name}>{att.name}</a>
                            <Button icon="pi pi-times" rounded text severity="danger" size="small" onClick={() => removeAttachment(isNew, newsId, idx)} aria-label="Rimuovi" style={{width: '2rem', height: '2rem'}} />
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const rowExpansionTemplate = (data: NewsHighlight) => {
        const detail = editingNews[data.id];
        if (!detail) return <div className="p-3">Caricamento dettagli...</div>;

        return (
            <div className="p-3 surface-ground border-round">
                <div className="grid p-fluid">
                    <div className="col-12 md:col-8 mb-3">
                        <label className="font-bold block mb-1">Titolo</label>
                        <InputText 
                            value={detail.title} 
                            onChange={(e) => setEditingNews(prev => ({ ...prev, [data.id]: { ...detail, title: e.target.value } }))}
                            className="font-bold text-xl" 
                        />
                    </div>
                    <div className="col-12 md:col-4 mb-3 flex flex-column justify-content-end">
                        <div className="flex align-items-center mb-2">
                            <Checkbox 
                                inputId={`highlight-${data.id}`} 
                                checked={detail.isHighlight || false} 
                                onChange={(e) => setEditingNews(prev => ({ ...prev, [data.id]: { ...detail, isHighlight: e.checked ?? false } }))} 
                            />
                            <label htmlFor={`highlight-${data.id}`} className="ml-2 font-bold cursor-pointer">In Evidenza</label>
                        </div>
                        <div className="flex gap-2 w-full">
                            <Button icon="pi pi-save" label="Salva" severity="success" onClick={() => handleUpdate(data.id)} className="flex-1" />
                            <Button icon="pi pi-trash" label="Elimina" severity="danger" onClick={() => handleDelete(data.id)} className="flex-1" />
                        </div>
                    </div>
                    <div className="col-12 mb-3">
                        <div className="flex align-items-center gap-3 mb-2">
                            <span className="font-bold">Aggiungi File/Immagine:</span>
                            <FileUpload 
                                mode="basic" 
                                auto 
                                customUpload 
                                uploadHandler={(e) => myUploader(e, false, data.id)} 
                                chooseLabel="Seleziona File" 
                            />
                        </div>
                        <small className="text-secondary block">Le immagini verranno aggiunte al testo, i file agli allegati.</small>
                        {renderAttachments(detail.attachments, false, data.id)}
                    </div>

                    <div className="col-12" data-color-mode="light">
                        <MDEditor
                            value={detail.contentMarkdown}
                            onChange={(val) => setEditingNews(prev => ({ ...prev, [data.id]: { ...detail, contentMarkdown: val || '' } }))}
                            height={400}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="manage-news-container">
            <div className="news-top-bar">
                <div className="news-heading">
                    <i className="pi pi-megaphone news-heading-icon"></i>
                    <div>
                        <h2>Gestione Notizie</h2>
                        <p>Crea, modifica e pubblica notizie.</p>
                    </div>
                </div>
                <Button
                    label={showNewDialog ? 'Elenco Notizie' : 'Nuova Notizia'}
                    icon={showNewDialog ? 'pi pi-list' : 'pi pi-plus'}
                    severity={showNewDialog ? 'secondary' : 'danger'}
                    onClick={() => showNewDialog ? closeNewEditor() : setShowNewDialog(true)}
                />
            </div>

            {!showNewDialog && (
                <section className="news-panel-card" aria-labelledby="news-list-title">
                    <div className="news-section-header">
                        <h3 id="news-list-title"><i className="pi pi-table"></i> Tutte le Notizie</h3>
                        <Button icon="pi pi-refresh" rounded text onClick={fetchNews} loading={loading} tooltip="Aggiorna elenco" />
                    </div>
                    <DataTable
                        value={newsList}
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        onRowExpand={onRowExpand}
                        rowExpansionTemplate={rowExpansionTemplate}
                        dataKey="id"
                        className="p-datatable-sm shadow-1 border-round overflow-hidden"
                        loading={loading}
                        emptyMessage="Nessuna notizia trovata. Clicca su 'Nuova Notizia' per crearne una."
                    >
                        <Column expander style={{ width: '5rem' }} />
                        <Column field="title" header="Titolo" sortable body={(rowData) => <strong>{rowData.title}</strong>} />
                        <Column field="subtitle" header="Sottotitolo" />
                        <Column
                            field="isHighlight"
                            header="In Evidenza"
                            body={(rowData) => (
                                <div className="flex align-items-center justify-content-center" onClick={(e) => e.stopPropagation()}>
                                    <InputSwitch checked={rowData.isHighlight || false} onChange={(e) => handleToggleHighlight(rowData.id, e.value || false)} />
                                </div>
                            )}
                            style={{ width: '8rem', textAlign: 'center' }}
                        />
                    </DataTable>
                </section>
            )}

            {showNewDialog && (
                <section className="news-panel-card news-create-panel" aria-labelledby="create-news-title">
                    <div className="news-section-header">
                        <div>
                            <h3 id="create-news-title"><i className="pi pi-file-edit"></i> Crea Nuova Notizia</h3>
                            <p>Completa le informazioni e il contenuto prima di pubblicare.</p>
                        </div>
                        <div className="news-header-actions">
                            <Button label="Annulla" icon="pi pi-times" severity="secondary" text onClick={closeNewEditor} />
                            <Button label="Salva Notizia" icon="pi pi-check" severity="danger" onClick={handleSaveNew} loading={saving} />
                        </div>
                    </div>

                    <div className="news-editor-form p-fluid">
                        <div className="news-form-section">
                            <h4><i className="pi pi-info-circle"></i> Informazioni principali</h4>
                            <div className="news-form-field">
                                <label htmlFor="new-news-title">Titolo <strong>*</strong></label>
                                <InputText id="new-news-title" value={newNews.title} onChange={(e) => setNewNews({ ...newNews, title: e.target.value })} placeholder="Inserisci il titolo della notizia" />
                            </div>
                            <div className="news-form-row">
                                <div className="news-form-field">
                                    <label htmlFor="new-news-author">Autore</label>
                                    <InputText id="new-news-author" value={newNews.author?.name} onChange={(e) => setNewNews({ ...newNews, author: { name: e.target.value, avatarUrl: newNews.author?.avatarUrl || '' } })} />
                                </div>
                                <div className="news-setting-field">
                                    <Checkbox inputId="new-highlight" checked={newNews.isHighlight || false} onChange={(e) => setNewNews({ ...newNews, isHighlight: e.checked ?? false })} />
                                    <label htmlFor="new-highlight">Metti la notizia <strong>in evidenza</strong></label>
                                </div>
                            </div>
                        </div>

                        <div className="news-form-section">
                            <h4><i className="pi pi-paperclip"></i> Immagini e allegati</h4>
                            <div className="news-upload-row">
                                <div>
                                    <strong>Aggiungi un file o un'immagine</strong>
                                    <small>Le immagini vengono inserite nel testo; gli altri file vengono aggiunti agli allegati.</small>
                                </div>
                                <FileUpload mode="basic" auto customUpload uploadHandler={(e) => myUploader(e, true)} chooseLabel="Seleziona File" />
                            </div>
                            {renderAttachments(newNews.attachments, true)}
                        </div>

                        <div className="news-form-section" data-color-mode="light">
                            <h4><i className="pi pi-align-left"></i> Contenuto della notizia <strong>*</strong></h4>
                            <p className="news-section-help">Usa la barra degli strumenti per formattare il testo. Il contenuto supporta Markdown.</p>
                            <MDEditor value={newNews.contentMarkdown} onChange={(val) => setNewNews({ ...newNews, contentMarkdown: val || '' })} height={400} />
                        </div>

                        <div className="news-form-actions">
                            <Button label="Annulla" icon="pi pi-times" severity="secondary" outlined onClick={closeNewEditor} />
                            <Button label="Salva Notizia" icon="pi pi-check" severity="danger" onClick={handleSaveNew} loading={saving} />
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default ManageNews;
